<template>
  <div class="feed-manager__page-base">
    <component
      :is="component"
      v-bind="pageProperties"
      @createFeed="openFeedModal"
      @editFeed="editFeed"
      @editSchedule="editSchedule"
      @scheduleFeed="openFrequencyModal"
      @deleteFeed="deleteFeed"
      @deleteSchedule="deleteSchedule"
      @downloadSchedule="downloadSchedule"
      @export="runFeed"
      @clickedRow="onRowClick"
      @refresh="refresh"
    />
    <ConfirmDialog
      v-if="showConfirmDialog"
      :visible="showConfirmDialog"
      :list="scheduleList"
      :tableType="view"
      :is-feed-running="isFeedRunning"
      @confirmDeleteFeed="confirmDeleteFeedAction"
      @confirmDeleteSchedule="confirmDeleteScheduleAction"
      @close="showConfirmDialog = false"
    />
    <scheduler-modal
      ref="modal"
      v-if="showSchedulerModal"
      :show-scheduler-modal="showSchedulerModal"
      :notebooks-provider="notebooksProvider"
      @close="closeFeedModal"
    />
    <frequency-modal
      v-if="showFrequencyModal"
      :showFrequencyModal="showFrequencyModal"
      :modal-loading="frequencyModalLoading"
      :save-schedule-response="saveScheduleResponse"
      :save-schedule-status="saveScheduleStatus"
      :has-duplicate-name="hasDuplicateName"
      :feed-id="feedId"
      @createSchedule="createSchedule"
      @close="closeModal"
      @hide-feedback-message="renameSchedule"
    />
    <flyover
      class="feed-manager__flyover"
      :class="{ 'feed-manager__flyover--open': showFlyover }"
      v-if="showFlyover"
      :flyover-tabs="flyoverTabs"
      :selected-row-info="selectedRowInfo"
      :show-flyover="showFlyover"
      :loading-flyover="loadingFlyover"
      :view="view"
      :flyover-provider="flyoverProvider"
      :reload-schedules-tab="reloadSchedules && saveSchedule"
      @closeFlyover="showFlyover = false"
      @scheduleFeed="openFrequencyModal"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SchedulerModal from '@/components/schedulerModal/SchedulerModal.vue'
import FrequencyModal from '@/components/frequencyModal/FrequencyModal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import Flyover from '@/components/flyover/Flyover.vue'
import { getConfigNotebookId } from '@/services/notebookDataProvider'
import { getActivityLogs } from '@/services/schedulesDataProvider'
import {
  runFeedOnce,
  checkFeedStatus,
  getSchedulesByFeedId,
} from '@/services/feedsDataProvider'
import { formatSchedule } from '@/utils/scheduleFormatter'
import { CANCEL_MESSAGE } from '@/components/schedulerModal/config/constants'
import CONFIG from './config'

export default {
  name: 'FeedManagerPage',
  components: {
    ConfirmDialog,
    SchedulerModal,
    FrequencyModal,
    Flyover,
  },
  data() {
    return {
      config: CONFIG,
      showConfirmDialog: false,
      scheduleList: [],
      selectedItem: '',
      notebooksProvider: { getConfigNotebookId },
      flyoverProvider: { getActivityLogs, getSchedulesByFeedId },
      showSchedulerModal: false,
      showFrequencyModal: false,
      frequencyModalLoading: false,
      feedId: undefined,
      saveScheduleStatus: false,
      saveScheduleResponse: false,
      showFlyover: false,
      selectedRowInfo: {},
      loadingFlyover: false,
      reloadSchedules: false,
      saveSchedule: false,
      isFeedRunning: null,
      hasDuplicateName: false,
    }
  },
  created() {
    this.fetch()
  },
  watch: {
    view() {
      this.fetch()
    },
    component() {
      this.showFlyover = false
    },
  },
  computed: {
    ...mapState({
      cache: (state) => state.cache.cache,
    }),
    view() {
      return this.$route.params.view
    },
    component() {
      this.refreshScroll()
      return this.config[this.view].component
    },
    records() {
      return this.cache[this.view] ?? []
    },
    token() {
      return this.cache.token
    },
    showLoader() {
      return this.cache.showLoader
    },
    pageProperties() {
      return {
        ...this.config[this.view].props,
        records: this.records,
        showLoader: this.showLoader,
      }
    },
    flyoverTabs() {
      return this.config[this.view].flyoverTabs
    },
  },
  methods: {
    async fetch() {
      this.$store.dispatch('fetchData', {
        key: this.view,
      })
    },
    async deleteFeed(feedId) {
      this.isFeedRunning = null
      this.showConfirmDialog = true
      this.selectedItem = feedId

      const [scheduleList, isFeedRunning] = await Promise.all([
        this.$store.dispatch('getSchedules', feedId),
        checkFeedStatus(feedId),
      ])

      this.scheduleList = scheduleList
      this.isFeedRunning = isFeedRunning
    },
    async deleteSchedule(scheduleId) {
      this.isFeedRunning = null
      this.showConfirmDialog = true
      this.selectedItem = scheduleId

      this.scheduleList = []
      this.isFeedRunning = false
    },
    async confirmDeleteFeedAction() {
      const isFeedDeleted = await this.$store.dispatch(
        'deleteFeed',
        this.selectedItem
      )
      if (isFeedDeleted) {
        this.handleNotification('success', 'deleteFeed')
      } else {
        this.handleNotification('error', 'error')
      }
      this.showConfirmDialog = false
    },
    async confirmDeleteScheduleAction() {
      const isScheduleDeleted = await this.$store.dispatch(
        'deleteSchedule',
        this.selectedItem
      )
      if (isScheduleDeleted) {
        this.handleNotification('success', 'deleteSchedule')
      } else {
        this.handleNotification('error', 'error')
      }
      this.showConfirmDialog = false
    },
    async openFeedModal(editMode = false, feedId = undefined) {
      if (!editMode) {
        // Let's reset the editedFeed in the store to undefined when creating a new feed.
        this.$store.dispatch('getSelectedFeed', undefined)
      }
      this.showSchedulerModal = true

      setTimeout(() => {
        this.$refs.modal.open(editMode, feedId)
      }, 10)
    },
    closeFeedModal() {
      this.showSchedulerModal = false
      this.$refs.modal.close()
      window.chrome?.webview?.postMessage(CANCEL_MESSAGE)
    },
    async openFrequencyModal(record, reloadSchedules = false) {
      // Since the frequency modal is called from different places,
      // let's find the feedId to associate with the new schedule.
      const feedId = record?.feed_id ?? record?.id ?? record
      this.frequencyModalLoading = true
      this.showFrequencyModal = true
      this.reloadSchedules = reloadSchedules

      await this.$store.dispatch('getSelectedFeed', Number(feedId))

      this.saveScheduleStatus = false
      this.saveScheduleResponse = false
      this.feedId = feedId

      this.frequencyModalLoading = false
    },
    async createSchedule(scheduleData) {
      this.saveSchedule = true
      this.frequencyModalLoading = true

      let scheduleResponse = {}
      if (!this.cache.editedSchedule) {
        scheduleResponse = await this.$store.dispatch('createSchedule', {
          feedId: this.feedId,
          scheduleInfo: formatSchedule(scheduleData),
        })
      } else {
        scheduleResponse = await this.$store.dispatch('updateSchedule', {
          feedId: this.cache.editedSchedule.feed_id,
          scheduleId: this.cache.editedSchedule.id,
          scheduleInfo: formatSchedule(scheduleData),
        })
      }

      const { schedule, hasDuplicateName } = scheduleResponse

      this.frequencyModalLoading = false
      this.reloadSchedules = false
      this.saveScheduleStatus = !!schedule
      this.saveScheduleResponse = true
      this.hasDuplicateName = hasDuplicateName
    },
    async downloadSchedule(event) {
      const [scheduleId, downloadEvent] = event
      // This is to prevent the flyover opening when we click on the Download button
      downloadEvent.stopPropagation()

      const downloadUrls = await this.$store.dispatch(
        'downloadSchedule',
        scheduleId
      )

      if (!downloadUrls) {
        this.handleNotification('error', 'errorDownload')
        return
      }

      if (downloadUrls.length) {
        for (const url of downloadUrls) {
          const link = document.createElement('a')
          link.href = url

          document.body.appendChild(link)
          link.click()
          link.remove()

          // Adding a delay to ensure the browser has enough time to process and initiate the download
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }
    },
    async runFeed(event) {
      const [feed, runEvent] = event
      const { id: feedId } = feed
      // This is to prevent the flyover opening when we click on the Run button
      runEvent.stopPropagation()
      const isFeedRunning = await checkFeedStatus(feedId)
      if (!isFeedRunning) {
        await this.triggerFeedRun(feedId)
      } else {
        this.handleNotification('warning', 'runningFeed')
      }
    },
    async triggerFeedRun(feedId) {
      const runFeedResponse = await runFeedOnce(feedId)
      if (runFeedResponse.status === 200) {
        this.handleNotification('informational', 'inProgressFeed')
      } else if (runFeedResponse.status === 409) {
        this.handleNotification('warning', 'runningFeed')
      } else {
        this.handleNotification('error', 'error')
      }
    },
    handleNotification(type, key) {
      this.$store.dispatch('pushNotification', {
        variation: type,
        title: `notification.${key}.title`,
        description: `notification.${key}.description`,
      })
    },
    onRowClick(event) {
      this.loadingFlyover = true
      this.showFlyover = true
      this.selectedRowInfo = event
    },
    async editFeed(feedId) {
      this.openFeedModal(true, feedId)
    },
    async editSchedule(schedule) {
      const { id: scheduleId, feedId } = schedule
      await this.$store.dispatch(
        'getSelectedSchedule',
        { scheduleId, feedId },
        feedId
      )
      this.openFrequencyModal(this.cache.editedSchedule)
    },
    closeModal() {
      this.$store.dispatch('getSelectedSchedule', {
        scheduleId: undefined,
        feedId: undefined,
      })
      window.chrome?.webview?.postMessage(CANCEL_MESSAGE)
      this.showFrequencyModal = false
    },
    async refresh() {
      this.showFlyover = false
      await this.$store.dispatch('fetchData', {
        key: this.view,
        refresh: true,
      })
    },
    refreshScroll() {
      const scrollElement = document.querySelector(
        '[class*="feed-manager__page-base"]'
      )
      scrollElement?.scrollTo(0, 0)
    },
    renameSchedule() {
      this.saveScheduleStatus = false
      this.saveScheduleResponse = false
      this.hasDuplicateName = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@mds/constants';
@import '@mds/typography';

.feed-manager__flyover {
  &--open {
    animation: flyover 0.3s ease-in-out;
  }
}
@keyframes flyover {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
